"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { agentService } from "../../voice-services/agentService";

interface WebhookSettingsProps {
  agentId: string;
  initialWebhookUrl?: string | null;
}

export const WebhookSettings: React.FC<WebhookSettingsProps> = ({
  agentId,
  initialWebhookUrl,
}) => {
  const [webhookUrl, setWebhookUrl] = useState<string>(initialWebhookUrl || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  // Set initial webhook URL when it becomes available
  useEffect(() => {
    if (initialWebhookUrl !== undefined) {
      setWebhookUrl(initialWebhookUrl || "");
    }
  }, [initialWebhookUrl]);

  const handleWebhookUrlUpdate = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await agentService.update(agentId, {
        agent_webhook_url: webhookUrl || null,
        webhook_url: null,
      });

      toast({
        title: "Webhook URL updated",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      console.error("Error updating webhook URL:", error);
      toast({
        title: "Error updating webhook URL",
        description:
          error?.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      // Reset to initial value on error
      if (initialWebhookUrl !== undefined) {
        setWebhookUrl(initialWebhookUrl || "");
      }
    } finally {
      setIsUpdating(false);
    }
  }, [agentId, webhookUrl, initialWebhookUrl, isUpdating, toast]);

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            bg="transparent"
            _hover={{ bg: "primary.50" }}
            _active={{ bg: "primary.100" }}
            _focusVisible={{
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            }}
            borderRadius="md"
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 3.125L15.9375 6.5625V13.4375L10 16.875L4.0625 13.4375V6.5625L10 3.125ZM5.93367 6.92356L10.0001 9.27775L14.0664 6.92359L10 4.56938L5.93367 6.92356ZM5.3125 8.00831V12.7168L9.37506 15.0688V10.3603L5.3125 8.00831ZM10.6251 15.0688L14.6875 12.7168V8.00836L10.6251 10.3603V15.0688Z"
                  fill="currentColor"
                />
              </svg>
              Webhook Settings
            </Box>
            <AccordionIcon color="primary.600" />
          </AccordionButton>
        </h2>

        <AccordionPanel
          pb={4}
          bg="primary.50"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
        >
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Agent Level Webhook URL
              </FormLabel>
              <Text fontSize="xs" color="gray.600" mb={2}>
                Webhook URL to receive events.
              </Text>
              <Input
                id="webhook-url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                onBlur={handleWebhookUrlUpdate}
                placeholder="https://your-webhook-url.com/events"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "gray.400",
                  boxShadow: "none",
                }}
              />
            </FormControl>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
